// API client para el módulo de pagos (ePayco)

import { api } from '@/contexts/AuthContext';
import { getOrFetchAuthenticatedResource } from '@/lib/authenticatedResource';
import type { InitiatePaymentRequest, EpaycoCheckoutParams, PaymentHistoryResponse, PaymentConfig } from './types';

declare global {
    interface Window {
        /** Required by ePayco browser tokenization (epayco.min.js uses `$`). */
        jQuery?: unknown;
        ePayco?: {
            setPublicKey: (key: string) => void;
            checkout?: {
                configure: (opts: { key: string; test: boolean }) => {
                    open: (data: Record<string, unknown>) => void;
                };
            };
            token?: {
                create: (
                    form: HTMLFormElement,
                    cb: (error: { data?: { description?: string } } | null, token: string) => void
                ) => void;
            };
        };
    }
}

const JQUERY_SRC = 'https://code.jquery.com/jquery-3.7.1.min.js';
const EPAYCO_TOKEN_SRC = 'https://checkout.epayco.co/epayco.min.js';
const EPAYCO_CHECKOUT_SRC = 'https://checkout.epayco.co/checkout.js';

let epaycoScriptPromise: Promise<void> | null = null;

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`No se pudo cargar ${src}`));
        document.head.appendChild(script);
    });
}

/**
 * Loads ePayco scripts for tokenization + Smart Checkout.
 * jQuery must come first — epayco.token.create calls `$` internally.
 */
export function loadEpaycoScript(): Promise<void> {
    if (typeof window === 'undefined') return Promise.resolve();
    if (window.jQuery && window.ePayco?.token && window.ePayco?.checkout) {
        return Promise.resolve();
    }
    if (epaycoScriptPromise) return epaycoScriptPromise;

    epaycoScriptPromise = (async () => {
        if (!window.jQuery) {
            await loadScript(JQUERY_SRC);
            if (!window.jQuery) {
                throw new Error('jQuery no disponible (requerido por ePayco tokenización)');
            }
        }
        await loadScript(EPAYCO_TOKEN_SRC);
        await loadScript(EPAYCO_CHECKOUT_SRC);
    })().catch((err) => {
        epaycoScriptPromise = null;
        throw err;
    });

    return epaycoScriptPromise;
}

const EPAYCO_SESSION_KEY = 'keyUserIndex';

function newEpaycoSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const part = () => Math.floor(65536 * (1 + Math.random())).toString(16).substring(1);
    return `${part()}${part()}-${part()}-${part()}-${part()}-${part()}${part()}${part()}`;
}

/**
 * Call right before ePayco.token.create. When the browser has no session id yet,
 * epayco.min.js registers `undefined` with token/encrypt but sends the new id to
 * token/tokenize, so the first card of every new browser fails with
 * "No se encontro el token de sesion".
 */
export function ensureEpaycoSession(): void {
    try {
        if (!window.localStorage.getItem(EPAYCO_SESSION_KEY)) {
            window.localStorage.setItem(EPAYCO_SESSION_KEY, newEpaycoSessionId());
        }
    } catch (err) {
        // Storage blocked: ePayco.js cannot keep a session either; let token.create report it.
        console.error('[ePayco] could not prepare the tokenization session', err);
    }
}

export async function getPaymentConfig(): Promise<PaymentConfig> {
    return getOrFetchAuthenticatedResource('payment-config', async () => {
        const response = await api.get<PaymentConfig>('/payments/config');
        return response.data;
    });
}

export async function initiatePayment(request: InitiatePaymentRequest): Promise<EpaycoCheckoutParams> {
    const response = await api.post<EpaycoCheckoutParams>('/payments/initiate', request);
    return response.data;
}

export async function getPaymentHistory(): Promise<PaymentHistoryResponse> {
    const response = await api.get<PaymentHistoryResponse>('/payments/history');
    return response.data;
}

/**
 * Abre ePayco Smart Checkout con los parámetros del backend.
 */
export async function openEpaycoCheckout(params: EpaycoCheckoutParams & { methods_disable?: string[] }): Promise<void> {
    await loadEpaycoScript();
    if (!window.ePayco?.checkout) {
        throw new Error('ePayco checkout no disponible');
    }

    const handler = window.ePayco.checkout.configure({
        key: params.public_key,
        test: !!params.test,
    });

    const data: Record<string, unknown> = {
        name: params.name || 'Reveca',
        description: params.description,
        invoice: params.invoice || params.reference_code,
        currency: params.currency,
        amount: params.amount,
        tax_base: params.tax_base || '0',
        tax: params.tax || '0',
        country: params.country || 'CO',
        lang: 'ES',
        external: 'false',
        response: params.response_url,
        confirmation: params.confirmation_url,
        email_billing: params.buyer_email,
        name_billing: params.buyer_full_name,
        extra1: params.extra1 || '',
        extra2: params.extra2 || '',
    };

    if (params.methods_disable?.length) {
        data.methodsDisable = params.methods_disable;
    }

    handler.open(data);
}
